import { Injectable, inject, signal } from '@angular/core';
import { Socket, io } from 'socket.io-client';
import { localStorageService } from 'src/app/services/localstorage.service';
import { environment } from 'src/environments/environment';

/**
 * An id as it arrives, as a number.
 *
 * The server is not consistent about this: the same `ts_id` comes through as
 * 68 on `ready` and the idle frame, and as "68" on the very next `screen`.
 * Everything downstream compares ids — which reader is this, which session is
 * this — and `"68" !== 68`, so a quoted id was silently dropping the live
 * view's frames and leaving the device stuck on "Awaiting Transaction".
 *
 * Coerced once, here, where the frames arrive. Null and undefined stay null:
 * a missing `ts_id` means "no session", which is different from session zero.
 */
function id(value: unknown): number | null {
  if (value === null || value === undefined || value === '') { return null; }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** One frame off the `/terminal` channel. */
interface TerminalEvent {
  type: string;
  data: any;
}

/**
 * One frame of the live view: what the reader is showing the customer.
 *
 * The frame carries no picture and will not: the artwork is this panel's own,
 * built from `flow` and `screen_name` into an `assets/terminal-screens/` path —
 * see LIVE-VIEW-ANSWERS.md §1 for why the backend declined to serve it.
 *
 * `ts_id` is null on the idle frame: the reader is between payments, which is
 * how the view knows the till is free rather than stuck on the last customer.
 */
/**
 * The plan the customer has actually chosen at the reader.
 *
 * An offer quotes a menu and the panel only pre-selects from it, so until this
 * existed the live view could only show the plan the panel sent — which is the
 * wrong one the moment the customer changes their mind at the counter.
 *
 * Only `service_id` comes from the device. The name, the money and the dates
 * are recomputed server-side from the offer and the business's fee terms, and
 * an id that is not on the session's offer is refused rather than echoed, so
 * these figures can be shown as authoritative.
 *
 * `charge_amount` is what leaves the customer's card today — the plan plus
 * their own transaction fee. The panel never adds that fee itself: it is a
 * function of the amount and the business's terms, and working it out here
 * would be a second implementation of pricing, free to drift from the one that
 * takes the money.
 */
export interface TerminalScreenSelection {
  service_id: number;
  name: string;
  /** The plan price, before the customer's fee. */
  plan_amount: number;
  customer_fee: number;
  /** What the customer pays now. The figure for "Payable Today". */
  charge_amount: number;
  interval_months: number;
  /** Already worded — "Monthly", "Every 3 months" — so nothing here interprets it. */
  interval_text: string;
  next_billing_date: string | null;
}

/**
 * The term a customer has settled on at the reader, for a financing contract.
 *
 * The server's `selection` for a contract frame, which is a different shape from
 * a subscription's and so is read into its own field rather than squeezed into
 * `TerminalScreenSelection` — that one is keyed on a `service_id` a contract
 * does not have, and a reader that required one would throw this away unread.
 *
 * Every figure is the server's own, priced from the contract's quote; the only
 * thing taken from the device is the month count. A term the contract was never
 * quoted arrives as no selection at all.
 */
export interface TerminalContractSelection {
  months: number;
  recurring_amount: number;
  /**
   * What the customer is charged each month: the instalment plus the fee on it.
   *
   * `recurring_amount` alone is the bare instalment. The Choose a Payment Plan
   * card prints the sum, so a screen that prints the figure uses this one or
   * the two disagree by the fee.
   */
  monthly: number;
  /** What the customer pays today on this term. */
  to_pay_today: number;
  plan_name: string;
  contract_type: number | null;
  /**
   * When the first monthly payment is taken, as the server worded it, or null.
   *
   * Not something the panel can know: it is chosen at the reader. Optional on
   * the wire, and read here only so that a server which starts sending it is
   * shown without another release — until then it is null and the screen draws
   * no date rather than one worked out here.
   */
  first_payment_date: string | null;
}

export interface TerminalScreenFrame {
  tr_id: number;
  ts_id?: number | null;
  flow?: string;
  screen_name?: string;
  display_name: string;
  step: number;
  total_steps: number;
  /**
   * The term the customer chose, on a contract frame. Absent on every other
   * flow, and on a contract frame until the reader reports a choice — in which
   * case the panel has no figures to draw for it and says so in words.
   */
  contract_selection?: TerminalContractSelection | null;
  /**
   * The plan the customer settled on, when the device has reported one.
   *
   * Optional on every frame: absent before they have chosen, on another
   * flow's frames, or when resolving it failed. Absence means "no change" —
   * the last one heard is carried forward below rather than blanked, because
   * a screen that dropped the amount between frames would read as the figure
   * being unknown.
   */
  selection?: TerminalScreenSelection | null;
  /** True when `watch` caught the panel up rather than the reader just moving. */
  replayed?: boolean;
  /** When the reader actually reported it — the original time on a replay. */
  at?: number | null;
}

/** One screen of a flow, as the `watch` reply lists them. */
export interface TerminalScreenInfo {
  screen_name: string;
  display_name: string;
  step: number;
  total_steps: number;
}

/** The flow a reader shows while it is not taking a payment. */
export const TERMINAL_IDLE_FLOW = 'terminal_idle';

/**
 * The one-off payment flow. The reader reports only three of its screens —
 * idle, card_prompt and success — whatever the catalogue lists, so its
 * step/total_steps are not a sequence the panel can draw progress from.
 * Failures and cancels arrive as a session status, never as a screen.
 */
export const TERMINAL_PP_FLOW = 'pp_terminal';

/** The flow a reader runs while a customer is enrolling on a plan. */
export const TERMINAL_SUBSCRIPTION_FLOW = 'subscription_terminal';

/**
 * The flow a reader runs while a customer chooses and finishes a financing
 * contract. Screen-only on the server: the reader's own app takes the signature
 * and the card, and the server makes the contract.
 */
export const TERMINAL_CONTRACT_FLOW = 'contract_terminal';

/* A session that is still running. Anything else — succeeded, canceled,
   failed, timed_out — has finished, and the server releases the reader and
   pushes the idle frame right behind it. */
const IN_FLIGHT = ['pending', 'dispatched', 'collecting'];

/* How long the last real screen is held after a session finishes.
   The failure arrives as three events in a row — the reader's `failed` screen,
   `session_update failed`, then the server's idle frame — so without this the
   view flips to "Terminal is free" before anyone has read why the payment did
   not go through (LIVE-VIEW-ANSWERS.md §3 q4). */
export const HOLD_AFTER_SESSION_MS = 6000;

/* Every frame in and out, in the browser console, while this is not a
   production build. The terminal is two processes and a reader away from the
   person debugging it, so "did the panel hear that" is the question asked most
   often — and the only honest answer is the traffic itself.

   Silent in production: these carry customer names and amounts. */
const TRACE = !environment.production;

function trace(direction: 'in' | 'out' | '·', label: string, detail?: unknown): void {
  if (!TRACE) { return; }
  const mark = direction === 'in' ? '◀' : direction === 'out' ? '▶' : '·';
  // eslint-disable-next-line no-console
  console.log(`%c[terminal] ${mark} ${label}`, 'color:#0a6b89;font-weight:600', detail ?? '');
}

/** The chosen plan off a frame, or null when it did not carry one. */
function readSelection(raw: any): TerminalScreenSelection | null {
  if (!raw || !raw.service_id) { return null; }
  return {
    service_id: Number(raw.service_id),
    name: String(raw.name || ''),
    plan_amount: Number(raw.plan_amount) || 0,
    customer_fee: Number(raw.customer_fee) || 0,
    charge_amount: Number(raw.charge_amount) || 0,
    interval_months: Number(raw.interval_months) || 1,
    interval_text: String(raw.interval_text || ''),
    next_billing_date: raw.next_billing_date || null,
  };
}

/**
 * The chosen term off a contract frame, or null when it did not carry one.
 *
 * Keyed on `months`, not `service_id`: see `TerminalContractSelection`. A
 * missing figure is a null selection rather than a zero one, because a zero
 * would be drawn as "Payable Today $0.00" in front of someone about to sign.
 */
function readContractSelection(raw: any): TerminalContractSelection | null {
  const months = Number(raw?.months);
  if (!Number.isFinite(months) || months <= 0) { return null; }
  if (raw.to_pay_today == null || raw.recurring_amount == null) { return null; }
  return {
    months,
    recurring_amount: Number(raw.recurring_amount),
    monthly: Number(raw.recurring_amount) + Number(raw.recurring_transaction_fee ?? raw.transaction_fee ?? 0),
    to_pay_today: Number(raw.to_pay_today),
    plan_name: String(raw.plan_name || ''),
    contract_type: raw.contract_type == null ? null : Number(raw.contract_type),
    first_payment_date: raw.first_payment_date || raw.start_date || null,
  };
}

/** What a reader is doing right now, as the socket reports it. */
export interface ReaderPresence {
  /** The device app is running and connected. A reader that is not cannot be sent to. */
  online: boolean;
  /** Another member of staff is already taking a payment on it. */
  busy: boolean;
  /**
   * Which session is holding it, when the frame says.
   *
   * The difference between "someone else is using this till" and "the payment
   * you just watched fail has not been closed yet". They read identically to
   * staff standing at a free reader, and only the first is a reason to go and
   * find another one.
   */
  busyTsId: number | null;
}

/**
 * The live half of the terminal screens.
 *
 * The shelf, the Send to Terminal button and the waiting state are all about
 * things that change while nobody is clicking: a reader is unplugged, a
 * colleague starts a payment on the till, a customer taps their card. Polling
 * reports all of that late, and cannot report presence at all — which is why
 * `collect` refuses with "That reader is not connected" rather than the panel
 * knowing not to offer the button.
 *
 * One namespace, `/terminal`, one channel, `terminal`, per
 * PANEL-TERMINAL-INTEGRATION.md §1. Deliberately no `tr_id` in `auth`: that is
 * what would make this connection a *device* rather than a panel.
 *
 * Separate from SocketsService because that one is the app-wide connection with
 * its own handlers on the default namespace; this is a second connection, to a
 * namespace of its own.
 *
 * Opened by the layout for as long as the panel is open, and by each terminal
 * screen on top of that — the count below keeps it to one connection. The
 * layout holds it because the readers ask the server whether anyone is at the
 * panel (`panel_online` on the device's hello): an order can only come from
 * here, so a business with nobody signed in should read as nobody there, not as
 * "the Devices screen is closed".
 */
@Injectable({ providedIn: 'root' })
export class FmTerminalSocket {

  private readonly local = inject(localStorageService);

  private socket: Socket | null = null;
  /** Screens currently using the connection. It closes when the last one goes. */
  private users = 0;

  /** Presence by `tr_id`, as reported. A reader not in here has not spoken yet. */
  readonly presence = signal<Record<number, ReaderPresence>>({});

  /** The reader being watched, if any, so a reconnect re-watches it. */
  private watching: number | null = null;

  /** Readers `probe` has asked about whose `ready` has not come back yet. */
  private readonly probing = new Set<number>();

  /**
   * Opens the connection, or joins the one already open.
   *
   * Call `release()` for every `connect()` — a screen that leaves without
   * releasing holds the socket open for the rest of the session.
   */
  connect(): void {
    this.users += 1;
    if (this.socket) { return; }

    const token = this.local.customGetItem('token') || '';
    this.socket = io(`${environment.socketBaseUrl}/terminal`, {
      path: '/api/socket.io',
      transports: ['websocket'],
      auth: { access_token: token },
    });

    this.socket.on('terminal', (event: TerminalEvent) => {
      trace('in', event?.type || '(no type)', event?.data);
      this.apply(event);
    });
    this.socket.on('connect', () => {
      trace('·', 'connected', { id: this.socket?.id, users: this.users });
      // The business room is joined server-side; a watch is ours to restate.
      if (this.watching) { this.watch(this.watching); }
      // So is a probe whose answer the dropped connection took with it.
      this.probing.forEach(trId => this.emitWatch(trId));
    });
    /* The two silent failures: a token the server will not take, and a socket
       that went away while the screen still believes it is listening. */
    this.socket.on('connect_error', (err: Error) => trace('·', 'connect_error', err?.message));
    this.socket.on('disconnect', (reason: string) => trace('·', 'disconnected', reason));
  }

  /** One fewer screen using it; the last one out closes the connection. */
  release(): void {
    this.users = Math.max(0, this.users - 1);
    if (this.users > 0 || !this.socket) { return; }
    trace('·', 'closing', { users: this.users });
    this.socket.disconnect();
    this.socket = null;
    this.watching = null;
    this.probing.clear();
    this.dropIdleHold();
    this.presence.set({});
    this.screens.set([]);
  }

  /**
   * Follows one reader's live view — screen name, step, and the rest.
   *
   * The server answers with the reader's current screen as well as its
   * catalogue, so the view fills straight away when the panel is opened
   * mid-payment. Whatever is on screen from a previous watch is cleared first:
   * the replay is about to say where this reader is, and until it lands the
   * honest answer is "connecting", not another till's frame.
   */
  watch(trId: number): void {
    trace('out', 'watch', { tr_id: trId });
    if (this.watching !== trId) {
      this.dropIdleHold();
      this.screen.set(null);
      this.screens.set([]);
    }
    this.watching = trId;
    this.emitWatch(trId);
  }

  /**
   * Asks how readers nobody is watching are right now.
   *
   * `reader_presence` is only sent when a device connects or disconnects, so a
   * picker opened while nothing is changing hears nothing about the readers it
   * lists — and unknown reads as live, so every row showed green. `ready`, the
   * reply to `watch`, is the one frame that says how a reader is as of now, so
   * each is watched just long enough to get it and then let go (see `apply`).
   * The reader being watched is skipped: its own watch already answers.
   */
  probe(trIds: number[]): void {
    for (const trId of trIds) {
      if (!trId || trId === this.watching || this.probing.has(trId)) { continue; }
      trace('out', 'probe', { tr_id: trId });
      this.probing.add(trId);
      this.emitWatch(trId);
    }
  }

  private emitWatch(trId: number): void {
    this.socket?.emit('terminal', { type: 'watch', data: { tr_id: trId } });
  }

  unwatch(trId: number): void {
    trace('out', 'unwatch', { tr_id: trId });
    if (this.watching === trId) { this.watching = null; }
    this.dropIdleHold();
    this.socket?.emit('terminal', { type: 'unwatch', data: { tr_id: trId } });
  }

  /**
   * A session this panel knows has ended — cancelled by staff, or seen closed
   * by the poll — so the reader it held is free.
   *
   * `busy` is only ever changed by the server's `reader_busy` frame, and that
   * frame is advisory: a socket that blinked, or a panel that reconnected
   * between the release and the broadcast, never hears it. The button then
   * read "Finishing the last payment on this reader" for good, because nothing
   * else clears it. The cancel call answering is the server saying the session
   * is released, so that answer is taken as the release — but only for the
   * session named, so a colleague's payment that started on the same reader a
   * moment later is not wiped.
   *
   * It then asks the server how the reader is as of now. If it really is still
   * held, the `ready` reply puts `busy` back; the local clear is a shortcut,
   * not a claim.
   */
  released(trId: number | null | undefined, tsId: number | null | undefined): void {
    if (!trId || !tsId) { return; }
    const state = this.presence()[trId];
    if (state?.busy && state.busyTsId === tsId) {
      this.merge(trId, { busy: false, busyTsId: null });
    }
    /* The watched reader answers in place; any other is probed, which lets it
       go again as soon as it has answered (see `apply`). */
    if (!this.socket) { return; }
    if (this.watching === trId) { this.emitWatch(trId); } else { this.probe([trId]); }
  }

  /** Whether this reader can be sent to: connected, and nobody else on it. */
  canSendTo(trId: number | null | undefined): boolean {
    if (!trId) { return false; }
    const state = this.presence()[trId];
    // Unknown means it has not reported yet. The button stays available and
    // `collect` refuses if it is genuinely offline — better than hiding an
    // option because a socket frame is late.
    return !state || (state.online && !state.busy);
  }

  /**
   * Why this reader cannot be sent to, when it is worth saying.
   *
   * `collect` refuses both of these, and a refusal after the press is a worse
   * way to learn the device is unplugged than a line under the button. Silent
   * while nothing is wrong, and silent for a reader that has not reported —
   * an unanswered reader is not reported as broken.
   *
   * Here rather than on each flow so the payment and subscription screens say
   * the same sentence about the same till.
   */
  /**
   * Why this reader cannot be sent to, when it is worth saying.
   *
   * `ours` is a session this panel has already seen end. A reader still
   * reported busy by that same session is not occupied by a colleague — it is
   * the one the staff member is standing at, holding a payment that failed
   * and has not been closed server-side yet. Telling them someone else is on
   * it sends them looking for another till that does not exist.
   */
  /**
   * Whether the only thing in the way is our own last payment closing.
   *
   * The difference between a problem and a pause. Everything else
   * `blockedReason` reports needs someone to go and do something — plug the
   * reader in, wait for a colleague to finish — while this clears itself in
   * about a second, so it should not be dressed as a failure.
   */
  heldByOurLastPayment(trId: number | null | undefined, ours: number | null = null): boolean {
    if (!trId || !ours) { return false; }
    const state = this.presence()[trId];
    return !!state?.busy && state.busyTsId === ours;
  }

  blockedReason(trId: number | null | undefined, ours: number | null = null): string {
    if (!trId) { return ''; }
    const state = this.presence()[trId];
    if (!state) { return ''; }
    if (!state.online) { return 'That reader is not connected. Check the device app is running.'; }
    if (!state.busy) { return ''; }
    /* Our own just-failed session still holding the reader. Said plainly:
       it is not a colleague on the till, it is the payment the staff member
       just watched fail, and the panel has already asked the server to close
       it — so this is a wait, not a problem to go and solve. */
    if (ours && state.busyTsId === ours) {
      return 'Finishing the last payment on this reader — it will be ready in a moment.';
    }
    return 'That reader is already taking a payment.';
  }


  /** What the reader is showing the customer, when one is being watched. */
  readonly screen = signal<TerminalScreenFrame | null>(null);

  /**
   * Every screen of the flow being watched, in order, as `watch` replies with
   * it. The authoritative list — the artwork folder is checked against this
   * rather than against the table in LIVE-VIEW-ANSWERS.md, which will age.
   */
  readonly screens = signal<TerminalScreenInfo[]>([]);

  /** The last session update heard, for a screen waiting on one. */
  readonly sessionUpdate = signal<{ ts_id: number; status: string; message?: string; advisory: boolean } | null>(null);

  /** When a session last finished, so the idle frame behind it can wait its turn. */
  private settledAt = 0;
  private idleHold: ReturnType<typeof setTimeout> | null = null;

  private dropIdleHold(): void {
    if (this.idleHold !== null) { clearTimeout(this.idleHold); }
    this.idleHold = null;
  }

  private apply(event: TerminalEvent): void {
    const data = event?.data || {};
    switch (event?.type) {
      case 'reader_presence':
        this.merge(id(data.tr_id) ?? 0, { online: !!data.app_connected });
        break;
      case 'reader_busy':
        this.merge(id(data.tr_id) ?? 0, {
          busy: !!data.busy,
          busyTsId: data.busy ? id(data.ts_id) : null,
        });
        break;
      /* The reply to `watch`: presence and busy state as of now, and the flow's
         screens. Presence is merged rather than assumed — this is the one frame
         that reports both halves at once, so neither has to be guessed. */
      case 'ready': {
        if (data.tr_id) {
          this.merge(id(data.tr_id) ?? 0, {
            online: !!(data.app_connected ?? data.online),
            busy: !!data.busy,
            busyTsId: data.busy ? id(data.ts_id) : null,
          });
        }
        /* A probe only wanted the presence. Let the reader go, and keep its
           catalogue off the one being watched — unless staff picked it while
           the answer was on its way, in which case this is their watch too. */
        const readyTr = id(data.tr_id);
        if (readyTr && this.probing.delete(readyTr) && readyTr !== this.watching) {
          this.socket?.emit('terminal', { type: 'unwatch', data: { tr_id: readyTr } });
          break;
        }
        if (Array.isArray(data.screens)) {
          this.screens.set(data.screens.map((row: any): TerminalScreenInfo => ({
            screen_name: String(row?.screen_name || ''),
            display_name: String(row?.display_name || row?.screen_name || ''),
            step: Number(row?.step) || 0,
            total_steps: Number(row?.total_steps) || 0,
          })));
        }
        break;
      }
      /* Two events, one piece of news.
       *
         `session_update` is the settled close and carries `status`.
         `session_result` is the device's own report and carries `outcome` —
         a reader that declined a card sends
         `{ type: "session_result", data: { ts_id, outcome: "failed" } }`.
         Same values, same consequence for the screen, so they are read
         together; what differs is how much they are worth, which is what
         `advisory` says.

         Matching only on `session_update`, and reading only `status`, is how
         a decline arrived as an event nothing matched: the panel sat on
         "waiting for the customer" until the poll caught up, and the hold
         below never started, so the idle frame pushed behind the failure
         wiped it off the mirror immediately. */
      /* `session_result` is the device's own name for this and is listed
         here for the reader's sake rather than because it fires: the server
         consumes the device's event and relays it to panels as
         `session_update` with `advisory: true`. One event, two sources —
         confirmed by backend, 2026-10-09. */
      /* The server telling watchers a session has been cancelled — including
         the one the panel asked for itself after a decline. Read as a settled
         close so the screen ends up in the same state it would reach by any
         other route; which wording it shows is the flow's business, and a
         decline already on screen outranks this. */
      case 'session_cancel':
      case 'session_result':
      case 'session_update': {
        const status = event.type === 'session_cancel'
          ? (data.status || data.outcome || 'canceled')
          : (data.status || data.outcome);
        /* The device's word unless the payload says otherwise. A reader can
           report a decline and then recover, so an advisory moves the screen
           but settles nothing.

           Inferred from the shape when the flag is missing, which is not
           defensive padding — it is the contract: the advisory frame carries
           `outcome`, the settled one carries `status`, never both. Reading
           `!!data.advisory` alone made an `{ ts_id, outcome }` frame look
           authoritative, which is the opposite of what it is, and took the
           branch that stops watching instead of the one that closes the
           session. */
        const advisory = data.advisory !== undefined
          ? !!data.advisory
          : event.type === 'session_result' || (!data.status && !!data.outcome);

        this.sessionUpdate.set({
          ts_id: id(data.ts_id) ?? 0,
          status,
          message: data.message,
          advisory,
        });

        /* The hold starts on either, advisory or not: the customer is looking
           at a declined card either way, and that is what must survive the
           idle frame the server pushes behind it. */
        if (status && !IN_FLIGHT.includes(status)) {
          this.settledAt = Date.now();
        }
        break;
      }
      /* `live_view_frame` is the same frame under the name the device app
         uses for it; `screen` is the server's. Both land here rather than one
         of them arriving to silence. */
      case 'live_view_frame':
      case 'screen': {
        /* There is one screen slot. A probed reader replays its current screen
           straight after `ready`, before the unwatch lands, and that frame must
           not push the watched reader's off the live view.

           A frame with no `tr_id` is taken as the watched reader's: frames are
           only sent to a panel that asked for them, and a device-composed one
           does not always name the reader it came from. Dropping it for want
           of an id would mean ignoring a frame sent to us and nobody else. */
        const trId = id(data.tr_id) ?? this.watching;
        if (this.watching && trId && trId !== this.watching) { break; }
        const frame: TerminalScreenFrame = {
          tr_id: trId ?? 0,
          /* Kept as null rather than dropped: null means "no session", which
             is different from "the frame did not say". */
          ts_id: id(data.ts_id),
          flow: data.flow,
          screen_name: data.screen_name,
          /* A screen the panel has no words for still reports its position,
             so the live view says something rather than going blank. */
          display_name: data.display_name || data.screen_name || 'Working…',
          step: Number(data.step) || 0,
          total_steps: Number(data.total_steps) || 0,
          /* Carried forward when a frame does not mention it: the customer
             chose once, and every screen after that is still about that plan.
             Only a frame for a different session starts again. */
          selection: readSelection(data.selection)
            ?? (this.screen()?.ts_id === id(data.ts_id) ? this.screen()?.selection ?? null : null),
          /* The same rule for a contract's chosen term: carried forward within
             a session, dropped for another. Read from the same `selection` key
             — the server sends a contract's under it — but by its own reader. */
          contract_selection: readContractSelection(data.selection)
            ?? (this.screen()?.ts_id === id(data.ts_id) ? this.screen()?.contract_selection ?? null : null),
          replayed: !!data.replayed,
          at: Number(data.at) || null,
        };
        /* Any real frame outranks an idle frame still waiting its turn. */
        this.dropIdleHold();
        /* The idle frame the server pushes on the heels of a finished session
           would wipe the success or failure the customer is still reading. So
           it is held rather than raced — and shown afterwards rather than
           thrown away, because the reader really is free by then. */
        const wait = HOLD_AFTER_SESSION_MS - (Date.now() - this.settledAt);
        if (frame.flow === TERMINAL_IDLE_FLOW && wait > 0) {
          this.idleHold = setTimeout(() => {
            this.idleHold = null;
            this.screen.set(frame);
          }, wait);
          break;
        }
        this.screen.set(frame);
        break;
      }
      case 'error':
        // Usually a watch for a reader belonging to another business. Nothing
        // on screen depends on it, so it is logged rather than shown.
        console.warn('[terminal socket]', data?.message);
        break;
    }
  }

  private merge(trId: number, part: Partial<ReaderPresence>): void {
    if (!trId) { return; }
    this.presence.update(all => {
      /* Defaults first, then what is already known, then what just arrived —
         a presence frame says nothing about busy, and a busy frame nothing
         about presence, so neither may wipe the other. */
      const current: ReaderPresence = all[trId] ?? { online: true, busy: false, busyTsId: null };
      return { ...all, [trId]: { ...current, ...part } };
    });
  }
}
